import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import type { Lead } from '../lib/api';

// Types for different modal states
export interface LeadModalState {
  isOpen: boolean;
  mode: 'create' | 'edit';
  editingLead?: Lead;
  prefillData?: {
    name?: string;
    headline?: string;
    companyName?: string;
    location?: string;
    profileUrl?: string;
    about?: string;
  };
}

export type ToastInput = Omit<ToastMessage, 'id'> & { id?: string };

interface GlobalModalContextType {
  // Lead Modal (create/edit)
  leadModal: LeadModalState;
  openNewLeadModal: (prefillData?: LeadModalState['prefillData']) => void;
  openEditLeadModal: (lead: Lead) => void;
  closeLeadModal: () => void;

  // Toast notifications
  showToast: (toast: ToastInput) => void;
  toasts: ToastMessage[];
  dismissToast: (id: string) => void;
}

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  description?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const GlobalModalContext = createContext<GlobalModalContextType | null>(null);

export function useGlobalModal() {
  const context = useContext(GlobalModalContext);
  if (!context) {
    throw new Error('useGlobalModal must be used within GlobalModalProvider');
  }
  return context;
}

interface GlobalModalProviderProps {
  children: ReactNode;
}

export function GlobalModalProvider({ children }: GlobalModalProviderProps) {
  // Lead Modal state (create/edit)
  const [leadModal, setLeadModal] = useState<LeadModalState>({
    isOpen: false,
    mode: 'create',
    editingLead: undefined,
    prefillData: undefined,
  });

  // Toast state
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Modal controls
  const openNewLeadModal = useCallback((prefillData?: LeadModalState['prefillData']) => {
    setLeadModal({ isOpen: true, mode: 'create', prefillData, editingLead: undefined });
  }, []);

  const openEditLeadModal = useCallback((lead: Lead) => {
    setLeadModal({ isOpen: true, mode: 'edit', editingLead: lead, prefillData: undefined });
  }, []);

  const closeLeadModal = useCallback(() => {
    setLeadModal({ isOpen: false, mode: 'create', prefillData: undefined, editingLead: undefined });
  }, []);

  // Toast controls
  const showToast = useCallback((toast: ToastInput) => {
    const id = toast.id || `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast = { ...toast, id };

    setToasts((prev) => [...prev, newToast]);

    // Auto-dismiss after duration (default 4 seconds)
    const duration = toast.duration ?? 4000;
    if (duration > 0) {
      setTimeout(() => {
        dismissToast(id);
      }, duration);
    }
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+N or Ctrl+N to open new lead modal
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        // Don't trigger if user is typing in an input/textarea
        const target = e.target as HTMLElement;
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

        if (!isInput) {
          e.preventDefault();
          openNewLeadModal();
        }
      }

      // Alternative: Cmd+Shift+L for "new Lead"
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'l') {
        e.preventDefault();
        openNewLeadModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openNewLeadModal]);

  const value: GlobalModalContextType = {
    leadModal,
    openNewLeadModal,
    openEditLeadModal,
    closeLeadModal,
    showToast,
    toasts,
    dismissToast,
  };

  return (
    <GlobalModalContext.Provider value={value}>
      {children}
    </GlobalModalContext.Provider>
  );
}
